/**
 * UBM Test Results Page - View all agent test results
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api";
import { toast } from "sonner";
import {
  FlaskConical,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Clock,
  Image,
  ExternalLink,
  ChevronLeft,
  RefreshCw,
  Loader2,
  Eye,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const STATUS_CONFIG = {
  passed: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/20", border: "border-green-500/30" },
  partial: { icon: AlertCircle, color: "text-yellow-400", bg: "bg-yellow-500/20", border: "border-yellow-500/30" },
  failed: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/20", border: "border-red-500/30" }
};

const WORKFLOW_LABELS = {
  bill_collector: "Bill Collector",
  payment_setup: "Payment Setup",
  pay_agent: "Pay Agent"
};

export default function UBMTestResults() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedResult, setExpandedResult] = useState(null);
  
  useEffect(() => {
    loadResults();
  }, [orgId]);
  
  const loadResults = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/ubm/${orgId}/test-results?limit=50`);
      setResults(res.data.results || []);
    } catch (error) {
      console.error("Failed to load test results:", error);
      if (error.response?.status === 403) {
        toast.error("UBM module not enabled for this organization");
        navigate(`/org/${orgId}`);
      } else {
        toast.error("Failed to load test results");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };
  
  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };
  
  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.failed;
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6" data-testid="ubm-test-results-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/org/${orgId}`)}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-cyan-400" />
              Test Results
            </h1>
            <p className="text-zinc-400 text-sm">View automated agent test results</p>
          </div>
        </div>
        
        <Button onClick={loadResults} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Results List */}
      <div className="space-y-4">
        {loading ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            </CardContent>
          </Card>
        ) : results.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center text-zinc-500">
              <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No test results yet</p>
              <p className="text-sm">Run tests from Agent Studio to see results here</p>
            </CardContent>
          </Card>
        ) : (
          results.map((result) => {
            const statusConfig = getStatusConfig(result.overall_status);
            const StatusIcon = statusConfig.icon;
            const isExpanded = expandedResult === result.test_run_id;
            
            return (
              <Collapsible
                key={result.test_run_id}
                open={isExpanded}
                onOpenChange={() => setExpandedResult(isExpanded ? null : result.test_run_id)}
              >
                <Card className={`bg-zinc-900 border-zinc-800 ${isExpanded ? 'border-l-4 border-l-cyan-500' : ''}`}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-zinc-800/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${statusConfig.bg}`}>
                            <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
                          </div>
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              Test Run
                              <Badge className={`${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}>
                                {result.overall_status}
                              </Badge>
                            </CardTitle>
                            <CardDescription className="flex items-center gap-4 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(result.started_at)}
                              </span>
                              {result.duration_seconds && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDuration(result.duration_seconds)}
                                </span>
                              )}
                              <span className="text-zinc-500">
                                {Object.keys(result.tests || {}).length} workflows tested
                              </span>
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {/* Workflow Status Pills */}
                          <div className="flex gap-2">
                            {Object.entries(result.tests || {}).map(([workflow, testData]) => {
                              const wfStatus = getStatusConfig(testData.status);
                              const WfIcon = wfStatus.icon;
                              return (
                                <Badge 
                                  key={workflow}
                                  variant="outline"
                                  className={`${wfStatus.color} ${wfStatus.border}`}
                                >
                                  <WfIcon className="w-3 h-3 mr-1" />
                                  {WORKFLOW_LABELS[workflow] || workflow}
                                </Badge>
                              );
                            })}
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-zinc-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-zinc-400" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="border-t border-zinc-800 pt-4 space-y-6">
                        {Object.entries(result.tests || {}).map(([workflow, testData]) => {
                          const wfStatus = getStatusConfig(testData.status);
                          const WfIcon = wfStatus.icon;
                          
                          return (
                            <div key={workflow} className="space-y-3">
                              <h3 className="text-lg font-medium flex items-center gap-2">
                                <WfIcon className={`w-5 h-5 ${wfStatus.color}`} />
                                {WORKFLOW_LABELS[workflow] || workflow}
                                <Badge variant="outline" className="text-xs">
                                  {testData.status}
                                </Badge>
                              </h3>
                              
                              {/* Screenshots Grid */}
                              {testData.screenshots?.length > 0 && (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                  {testData.screenshots.map((screenshot, idx) => (
                                    <Card key={idx} className="bg-zinc-800 border-zinc-700 overflow-hidden">
                                      <CardContent className="p-3">
                                        <div className="text-sm font-medium mb-1 truncate">
                                          {screenshot.stage}
                                        </div>
                                        <p className="text-xs text-zinc-500 mb-2 line-clamp-2">
                                          {screenshot.description}
                                        </p>
                                        {screenshot.s3_url ? (
                                          <a 
                                            href={screenshot.s3_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block"
                                          >
                                            <div className="aspect-video bg-zinc-900 rounded border border-zinc-700 flex items-center justify-center hover:border-cyan-500 transition-colors">
                                              <div className="text-center">
                                                <ExternalLink className="w-6 h-6 text-cyan-400 mx-auto mb-1" />
                                                <span className="text-xs text-zinc-400">View</span>
                                              </div>
                                            </div>
                                          </a>
                                        ) : (
                                          <div className="aspect-video bg-zinc-900 rounded border border-zinc-700 flex items-center justify-center">
                                            <Image className="w-6 h-6 text-zinc-600" />
                                          </div>
                                        )}
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              )}
                              
                              {/* Errors */}
                              {testData.errors?.length > 0 && (
                                <Card className="bg-red-900/20 border-red-500/30">
                                  <CardContent className="p-3">
                                    <h4 className="text-sm font-medium text-red-400 mb-2">Errors</h4>
                                    <ul className="space-y-1">
                                      {testData.errors.map((error, idx) => (
                                        <li key={idx} className="text-xs text-red-300 font-mono">
                                          • {error}
                                        </li>
                                      ))}
                                    </ul>
                                  </CardContent>
                                </Card>
                              )}
                              
                              {/* Downloaded Files */}
                              {testData.downloaded_files?.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  <span className="text-sm text-zinc-400">Downloaded:</span>
                                  {testData.downloaded_files.map((file, idx) => (
                                    <a
                                      key={idx}
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      {file.filename || `File ${idx + 1}`}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })
        )}
      </div>
    </div>
  );
}
